// Tabs/buildSessionTabs.ts
import type { FacilitatorSessionView } from '@actschurch/ccms-sdk';

export type SessionTabEntry =
  | { kind: 'special'; key: string; title: string; session: FacilitatorSessionView }
  | { kind: 'normal'; key: 'sessions'; title: 'Sessions'; sessions: FacilitatorSessionView[] };

/**
 * Groups a facilitator's sessions into tabs — a Dutch-flag-style
 * three-way partition around orderNumber:
 * - Special sessions each keep their own position/tab.
 * - Normal sessions merge into a single "Sessions" tab.
 *
 * FacilitatorSessionView carries orderNumber directly, so no
 * courseSessionID -> orderNo lookup map is needed anymore.
 */
export function buildSessionTabs(sessions: FacilitatorSessionView[]): SessionTabEntry[] {
  const sorted = [...sessions]
    .map((session, index) => ({ session, index }))
    .sort((a, b) => a.session.orderNumber - b.session.orderNumber || a.index - b.index)
    .map((entry) => entry.session);

  const tabs: SessionTabEntry[] = [];
  let normalTab: Extract<SessionTabEntry, { kind: 'normal' }> | null = null;

  for (const session of sorted) {
    if (session.isSpecial) {
      tabs.push({
        kind: 'special',
        key: `special-${session.registrationSessionID}`,
        title: session.name ?? `Session ${session.registrationSessionID}`,
        session,
      });
    } else {
      if (!normalTab) {
        normalTab = { kind: 'normal', key: 'sessions', title: 'Sessions', sessions: [] };
        tabs.push(normalTab);
      }
      normalTab.sessions.push(session);
    }
  }

  return tabs;
}